// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { observable, action, makeObservable, computed, runInAction } from 'mobx';
import { AxiosRequestConfig } from 'axios';

import container from '@core/di';
import DataManagementService from '@shared/services/data-management';
import { ReportInformationType, ExportInformationType, ImportInformationType } from '@shared/constants/data-management';
import { ReportsService } from '@shared/services';
import { FilterItems } from '@shared/models/jobrequesttimelines';

// Class implementing file ops (import, export, report, ...)
@injectable()
export default class DataManagementStore {
  static diToken = Symbol('data-management-store');
  private service = container.get<DataManagementService>(DataManagementService.diToken);
  private reportsService = container.get<ReportsService>(ReportsService.diToken);
  @observable _loading = false;

  constructor() {
    
    makeObservable(this);
  }

  @computed get loading() {
    return this._loading;
  }

  @action reportData = async (reportType: ReportInformationType, orderId: number, mapBoundary: string, filters: Array<FilterItems> | undefined, config: AxiosRequestConfig) => {
    runInAction(() => {
      this._loading = true;
    });

    try {
      // Build the query for each report type
      //let queries = '{\nassets (orderId : 4)\n{\nsite,\nflightCollection,\n}\n}';
      const queries = this.reportsService.getReportQuery(reportType, orderId, mapBoundary, filters);
      const blobparts = await this.service.reportData(reportType, queries, config);

      runInAction(() => {
        this._loading = false;
      });

      return blobparts;
    } 
    catch (err) {
      runInAction(() => {
        this._loading = false;
      });

      throw err;
    }
  }

  @action exportData = async (exportType: ExportInformationType, config: AxiosRequestConfig) => {
    runInAction(() => {
      this._loading = true;
    });

    try {
      const blobparts = await this.service.exportData(exportType, config);

      runInAction(() => {
        this._loading = false;
      });

      return blobparts;
    } 
    catch (err) {
      runInAction(() => {
        this._loading = false;
      });

      throw err;
    }
  }

  // Call service method to import (upload to portal-api) a file.
  @action importData = async (file: File, type: ImportInformationType) => {
    runInAction(() => {
      this._loading = true;
    });

    try {
      await this.service.importData(file, type);

      runInAction(() => {
        this._loading = false;
      });
    } 
    catch (err) {
      runInAction(() => {
        this._loading = false;
      });
      throw err;
    }
  }

  // Call service method to check if import data has the correct format.
  @action validateImport = async (file: File, type: ImportInformationType) => {
    runInAction(() => {
      this._loading = true;
    });

    try {
      const data = await this.service.validateImport(file, type);
      runInAction(() => {
        this._loading = false;
      });

      return data;
    } 
    catch (err) {
      runInAction(() => {
        this._loading = false;
      });
      throw err;
    }
  }
}